import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Briefcase, MapPin, Clock, CheckCircle, Users, Code, Lock, Globe, ChevronRight } from 'lucide-react';
import Button from '../components/atoms/Button';
import Card from '../components/atoms/Card';
import ResponsiveContainer from '../components/molecules/ResponsiveContainer';

const CareersPage: React.FC = () => {
  const navigate = useNavigate();

  // Sample job listings
  const jobListings = [
    {
      id: 'job-1',
      title: 'Senior Blockchain Engineer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      description: 'Design and implement secure blockchain solutions for document notarization and verification.',
      requirements: [
        '5+ years of software engineering experience',
        '3+ years working with blockchain technologies (Ethereum, Algorand)',
        'Experience with smart contract development',
        'Strong understanding of cryptography and security principles'
      ]
    },
    {
      id: 'job-2',
      title: 'Frontend Developer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      description: 'Build beautiful, responsive, and secure user interfaces for our document management platform.',
      requirements: [
        '3+ years of frontend development experience',
        'Proficiency in React, TypeScript, and modern CSS',
        'Experience with state management and API integration',
        'Understanding of security best practices in frontend development'
      ]
    },
    {
      id: 'job-3',
      title: 'Security Researcher',
      department: 'Security',
      location: 'Remote',
      type: 'Full-time',
      description: 'Identify and address security vulnerabilities in our platform and encryption implementations.',
      requirements: [
        'Background in information security or cryptography',
        'Experience with penetration testing and security audits',
        'Knowledge of common web application vulnerabilities',
        'Understanding of blockchain security models'
      ]
    },
    {
      id: 'job-4',
      title: 'Product Manager',
      department: 'Product',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead the development of new features and improvements to our secure document management platform.',
      requirements: [
        '3+ years of product management experience',
        'Experience with security or blockchain products',
        'Strong understanding of user-centered design principles',
        'Excellent communication and stakeholder management skills'
      ]
    }
  ];

  // Benefits
  const benefits = [
    {
      title: 'Remote-First Culture',
      description: 'Work from anywhere in the world with flexible hours.',
      icon: Globe
    },
    {
      title: 'Competitive Compensation',
      description: 'Salary, equity, and performance bonuses.',
      icon: Briefcase
    },
    {
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance and wellness programs.',
      icon: Shield
    },
    {
      title: 'Professional Growth',
      description: 'Learning budget and career development opportunities.',
      icon: Users
    },
    {
      title: 'Cutting-Edge Tech',
      description: 'Work with the latest in blockchain and security technology.',
      icon: Code
    },
    {
      title: 'Work-Life Balance',
      description: 'Unlimited PTO and flexible working hours.',
      icon: Clock
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                icon={ArrowLeft}
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900"
              >
                Back to Home
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                  <Briefcase className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Careers</h1>
                  <p className="text-gray-600">Join our team at GravexLabs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <ResponsiveContainer maxWidth="full" padding="none">
          <div className="space-y-16">
            {/* Hero Section */}
            <div className="text-center max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Join Us in Securing the Digital Future
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  At GravexLabs, we're building Aegis Vault to revolutionize how people secure, share, and manage their most important documents. We're looking for passionate individuals to join our mission.
                </p>
                <div className="flex justify-center">
                  <div className="w-24 h-1 bg-primary-500 rounded-full"></div>
                </div>
              </motion.div>
            </div>

            {/* Our Culture */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="rounded-xl overflow-hidden shadow-xl"
              >
                <img 
                  src="https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Team collaboration" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Culture</h3>
                <div className="space-y-4 text-gray-600">
                  <p>
                    At GravexLabs, we believe that great security comes from great people. We've built a culture that values innovation, transparency, and work-life balance.
                  </p>
                  <p>
                    As a remote-first company, we hire the best talent regardless of location. Our team is diverse, distributed, and united by a shared mission to make the digital world more secure.
                  </p>
                  <p>
                    We value deep work, asynchronous communication, and results over hours. We trust our team members to manage their time effectively and provide the support they need to do their best work.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Benefits */}
            <div>
              <div className="text-center mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Benefits & Perks</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  We believe in taking care of our team with competitive compensation and benefits that support your well-being and growth.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <Card className="h-full p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <benefit.icon className="h-6 w-6 text-primary-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h4>
                          <p className="text-gray-600">{benefit.description}</p>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Open Positions */}
            <div>
              <div className="text-center mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Open Positions</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Join our team and help build the future of secure document management. We're always looking for talented individuals who share our passion.
                </p>
              </div>
              <div className="space-y-6">
                {jobListings.map((job, index) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <Card className="p-6">
                      <div className="md:flex md:items-center md:justify-between">
                        <div>
                          <h4 className="text-xl font-semibold text-gray-900 mb-2">{job.title}</h4>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-4">
                            <span className="flex items-center gap-1">
                              <Briefcase size={14} />
                              {job.department}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {job.type}
                            </span>
                          </div>
                          <p className="text-gray-600 mb-4">{job.description}</p>
                          <div className="space-y-2 mb-4">
                            <h5 className="font-medium text-gray-900">Requirements:</h5>
                            <ul className="space-y-1">
                              {job.requirements.map((req, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                  <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                                  <span>{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="mt-4 md:mt-0">
                          <Button
                            variant="primary"
                            icon={ChevronRight}
                            iconPosition="right"
                          >
                            Apply Now
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* No Positions Section */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Don't See a Position That Fits?</h3>
              <p className="text-gray-600 max-w-2xl mx-auto mb-6">
                We're always interested in connecting with talented individuals who are passionate about security, blockchain, and building great products. Send us your resume and let us know how you can contribute to our mission.
              </p>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </Button>
            </div>

            {/* Company Values */}
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h3>
              <p className="text-gray-600 max-w-2xl mx-auto mb-8">
                These core principles guide everything we do at GravexLabs, from product development to how we work together as a team.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-6">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                      <Lock className="h-8 w-8 text-primary-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Security First</h4>
                    <p className="text-gray-600 text-center">
                      We believe security should never be an afterthought. It's built into everything we do.
                    </p>
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                      <Users className="h-8 w-8 text-primary-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">User-Centric</h4>
                    <p className="text-gray-600 text-center">
                      We build solutions that prioritize user experience without compromising security.
                    </p>
                  </div>
                </Card>
                <Card className="p-6">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                      <Code className="h-8 w-8 text-primary-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Innovation</h4>
                    <p className="text-gray-600 text-center">
                      We're constantly pushing the boundaries of what's possible with blockchain and encryption technology.
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CareersPage;